import { useState, useRef, useEffect } from 'react';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'zh-CN', label: '中文', flag: '🇨🇳' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'id', label: 'Indonesia', flag: '🇮🇩' },
  { code: 'sw', label: 'Kiswahili', flag: '🇰🇪' },
];

function getCookie(name: string): string {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : '';
}

function getCurrentLang(): string {
  const cookie = getCookie('googtrans');
  if (!cookie) return 'en';
  const parts = cookie.split('/');
  return parts[parts.length - 1] || 'en';
}

interface TranslateButtonProps {
  scrolled?: boolean;
}

export default function TranslateButton({ scrolled = false }: TranslateButtonProps) {
  const [open, setOpen] = useState(false);
  const [activeLang, setActiveLang] = useState(getCurrentLang());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (code: string) => {
    setActiveLang(code);
    setOpen(false);

    if (code === 'en') {
      // Reset to original
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
      window.location.reload();
      return;
    }

    // Set Google Translate cookie
    const cookieVal = `/en/${code}`;
    document.cookie = `googtrans=${cookieVal}; path=/`;
    document.cookie = `googtrans=${cookieVal}; path=/; domain=${window.location.hostname}`;

    // Trigger Google Translate
    const select = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
    if (select) {
      select.value = code;
      select.dispatchEvent(new Event('change'));
    } else {
      window.location.reload();
    }
  };

  const current = languages.find((l) => l.code === activeLang) || languages[0];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
          scrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/80 hover:bg-white/10'
        }`}
        aria-label="Translate page"
      >
        <i className="ri-translate-2 text-base" />
        <span className="hidden xl:inline">{current.flag} {current.label}</span>
        <span className="xl:hidden">{current.flag}</span>
        <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 bg-white rounded-xl py-1.5 z-[200] border border-gray-100 min-w-[160px]"
          style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}
        >
          <p className="px-3 py-1.5 text-xs font-black uppercase tracking-widest text-gray-400 border-b border-gray-100 mb-1">
            Select Language
          </p>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors text-left ${
                activeLang === lang.code
                  ? 'font-black text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              style={activeLang === lang.code ? { backgroundColor: '#0a2342' } : {}}
            >
              <span className="text-base leading-none">{lang.flag}</span>
              <span>{lang.label}</span>
              {activeLang === lang.code && <i className="ri-check-line ml-auto text-xs" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
